import { Trash2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  baseQuantityOf,
  changeLevel,
  currentLevel,
} from '../lib/purchase-order-lines'
import type { EditLine } from '../lib/purchase-order-lines'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'
import { cn } from '@/lib/utils'

/**
 * One order line (CHG-2026-10-edi-purchase-orders, design UI), laid out like a delivery line:
 * a table row once the list is wide enough (container query on '@container/lines'), a card with
 * labeled fields below that. Every value has its own field — Gebinde, GTIN, Menge and Stück are
 * never folded into the article name.
 */
export const LINES_CONTAINER = '@container/lines'

/** Columns: Artikel, Gebinde, GTIN, Menge, = Stück, [remove]. */
const COLUMNS = {
  edit: '@3xl/lines:grid-cols-[minmax(10rem,1fr)_11rem_9rem_6rem_6rem_2.5rem]',
  readOnly: '@3xl/lines:grid-cols-[minmax(10rem,1fr)_11rem_9rem_6rem_6rem]',
} as const

const ROW_BASE =
  'grid grid-cols-3 items-start gap-x-3 gap-y-3 border-t px-3 py-3 @3xl/lines:items-center'
const HEADER_BASE =
  'hidden gap-x-3 bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground @3xl/lines:grid'

function Field({
  label,
  numeric = true,
  className,
  children,
}: {
  label: string
  numeric?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
      <span className="text-xs text-muted-foreground @3xl/lines:hidden">
        {label}
      </span>
      <div
        className={cn(
          'min-w-0',
          numeric && 'tabular-nums @3xl/lines:text-right',
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function PurchaseOrderLinesHeader({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('purchase-orders')
  return (
    <div
      className={cn(HEADER_BASE, readOnly ? COLUMNS.readOnly : COLUMNS.edit)}
    >
      <div>{t('detail.column.article')}</div>
      <div>{t('detail.column.package')}</div>
      <div>{t('detail.column.gtin')}</div>
      <div className="text-right">{t('detail.column.quantity')}</div>
      <div className="text-right">{t('detail.column.pieces')}</div>
      {!readOnly && <div />}
    </div>
  )
}

export function PurchaseOrderLineRow({
  line,
  readOnly = false,
  onChange,
  onRemove,
}: {
  line: EditLine
  readOnly?: boolean
  onChange?: (next: EditLine) => void
  onRemove?: () => void
}) {
  const { t } = useTranslation('purchase-orders')
  const level = currentLevel(line)
  const pieces = baseQuantityOf(line)
  const levelLabel = (name: string | null, content: number) =>
    t('detail.levelOption', {
      name: name ?? t('detail.levelUnnamed'),
      content: formatStockDisplay(content),
    })

  return (
    <div className={cn(ROW_BASE, readOnly ? COLUMNS.readOnly : COLUMNS.edit)}>
      <div
        className={cn(
          'min-w-0 @3xl/lines:col-span-1',
          readOnly ? 'col-span-3' : 'col-span-2',
        )}
      >
        <p className="font-medium break-words">{line.name ?? line.sku}</p>
        <p className="font-mono text-xs text-muted-foreground">{line.sku}</p>
      </div>

      {!readOnly && (
        <div className="col-start-3 row-start-1 flex justify-end @3xl/lines:order-last @3xl/lines:col-start-auto @3xl/lines:row-start-auto">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`${t('detail.removeLine')} ${line.sku}`}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Field
        label={t('detail.column.package')}
        numeric={false}
        className="col-span-3 @3xl/lines:col-span-1"
      >
        {readOnly || line.levels.length === 0 ? (
          <span>{levelLabel(level.name, level.contentPerUnit)}</span>
        ) : (
          <Select
            value={String(line.packageLevel)}
            onValueChange={(v) => onChange?.(changeLevel(line, Number(v)))}
          >
            <SelectTrigger
              className="w-full"
              aria-label={`${t('detail.column.package')} ${line.sku}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {line.levels.map((l) => (
                <SelectItem
                  key={l.level}
                  value={String(l.level)}
                  disabled={!l.orderable}
                >
                  {l.orderable
                    ? levelLabel(l.name, l.contentPerUnit)
                    : t('detail.levelNotOrderable', {
                        level: levelLabel(l.name, l.contentPerUnit),
                        reason: t(
                          `notOrderable.reason.${l.reason ?? 'no_gtin'}`,
                        ),
                      })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      <Field label={t('detail.column.gtin')} numeric={false}>
        <span className="font-mono text-sm break-all">{level.gtin ?? '–'}</span>
      </Field>

      <Field label={t('detail.column.quantity')}>
        {readOnly ? (
          line.quantity
        ) : (
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={line.quantity}
            aria-label={`${t('detail.column.quantity')} ${line.sku}`}
            className="text-right tabular-nums"
            onChange={(e) => onChange?.({ ...line, quantity: e.target.value })}
          />
        )}
      </Field>

      <Field label={t('detail.column.pieces')}>
        {pieces == null ? '–' : formatStockDisplay(pieces)}
      </Field>
    </div>
  )
}
